import React from 'react';
import { format } from 'date-fns';
import { ClockIcon } from '@heroicons/react/24/outline';

const TimeSlotSelector = ({ slots, selectedSlot, onSlotSelect, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Select Time</h2>
        <div className="grid grid-cols-3 gap-3">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!slots || slots.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Select Time</h2>
        <div className="text-center py-8">
          <ClockIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No available time slots for this date</p>
          <p className="text-sm text-gray-400 mt-1">Please select another date</p>
        </div>
      </div>
    );
  }

  // Group slots by morning, afternoon, evening
  const groupSlots = (slots) => {
    const groups = {
      morning: [], // 6am - 12pm
      afternoon: [], // 12pm - 5pm
      evening: [], // 5pm - 9pm
    };

    slots.forEach((slot) => {
      const hour = new Date(slot.startTime).getHours();
      if (hour < 12) {
        groups.morning.push(slot);
      } else if (hour < 17) {
        groups.afternoon.push(slot);
      } else {
        groups.evening.push(slot);
      }
    });

    return groups;
  };

  const groupedSlots = groupSlots(slots);

  const renderSlotGroup = (title, slots) => {
    if (slots.length === 0) return null;

    return (
      <div className="mb-6 last:mb-0">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
          {title}
        </h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {slots.map((slot) => {
            const isSelected = selectedSlot && selectedSlot.startTime === slot.startTime;
            const isAvailable = slot.available;

            return (
              <button
                key={slot.startTime}
                onClick={() => isAvailable && onSlotSelect(slot)}
                disabled={!isAvailable}
                className={`
                  py-2 px-3 rounded-lg text-sm font-medium transition-all
                  ${isSelected ? 'bg-primary-500 text-white ring-2 ring-primary-500 ring-offset-2' : ''}
                  ${!isSelected && isAvailable ? 'bg-white border border-gray-200 text-gray-700 hover:border-primary-500 hover:bg-primary-50' : ''}
                  ${!isAvailable ? 'bg-gray-50 text-gray-400 cursor-not-allowed line-through' : ''}
                `}
              >
                {format(new Date(slot.startTime), 'h:mm a')}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Select Time</h2>

      {renderSlotGroup('Morning', groupedSlots.morning)}
      {renderSlotGroup('Afternoon', groupedSlots.afternoon)}
      {renderSlotGroup('Evening', groupedSlots.evening)}

      {selectedSlot && (
        <div className="mt-6 p-3 bg-primary-50 rounded-lg flex items-center justify-center">
          <ClockIcon className="w-5 h-5 text-primary-600 mr-2" />
          <p className="text-sm text-primary-800 font-medium">
            {format(new Date(selectedSlot.startTime), 'h:mm a')} -{' '}
            {format(new Date(selectedSlot.endTime), 'h:mm a')}
          </p>
        </div>
      )}
    </div>
  );
};

export default TimeSlotSelector;
